import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface TenantContextType {
  activeTenantId: string | null;
  activeTenantName: string | null;
  userDefaultTenantId: string | null;
  setActiveTenantId: (tenantId: string) => void;
  isLoading: boolean;
  isSwitched: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [userDefaultTenantId, setUserDefaultTenantId] = useState<string | null>(null);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [activeTenantName, setActiveTenantName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserTenant = async () => {
      if (!user) {
        setUserDefaultTenantId(null);
        setActiveTenantId(null);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('tenant_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('🏢 TenantContext: Error fetching profile:', error);
          throw error;
        }

        if (!data) {
          console.warn('🏢 TenantContext: No profile found for user:', user.id);
          setUserDefaultTenantId(null);
          setActiveTenantId(null);
          setActiveTenantName(null);
          setIsLoading(false);
          return;
        }
        
        const tenantId = data.tenant_id;
        setUserDefaultTenantId(tenantId);
        
        console.log('🏢 TenantContext: User default tenant ID:', tenantId);
        
        // Check localStorage for saved tenant switch
        const savedTenantId = localStorage.getItem('activeTenantId');
        const targetTenantId = savedTenantId || tenantId;
        setActiveTenantId(targetTenantId);
        
        console.log('🏢 TenantContext: Active tenant ID:', targetTenantId, savedTenantId ? '(switched)' : '(default)');
        
        // Fetch tenant name
        if (targetTenantId) {
          const { data: tenantData, error: tenantError } = await supabase
            .from('tenants')
            .select('name')
            .eq('id', targetTenantId)
            .maybeSingle();
          
          if (tenantError) {
            console.error('🏢 TenantContext: Error fetching tenant name:', tenantError);
          }
          
          const tenantName = tenantData?.name || null;
          setActiveTenantName(tenantName);
          console.log('🏢 TenantContext: Active tenant name:', tenantName, 'from tenant ID:', targetTenantId);
        }
      } catch (error) {
        console.error('🏢 TenantContext: Fatal error fetching tenant:', error);
        setUserDefaultTenantId(null);
        setActiveTenantId(null);
        setActiveTenantName(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserTenant();
  }, [user]);

  const handleSetActiveTenantId = async (tenantId: string) => {
    setActiveTenantId(tenantId);
    localStorage.setItem('activeTenantId', tenantId);
    
    console.log('🏢 TenantContext: Switching to tenant:', tenantId);
    
    // Fetch and update tenant name
    const { data: tenantData, error: tenantError } = await supabase
      .from('tenants')
      .select('name')
      .eq('id', tenantId)
      .maybeSingle();
    
    if (tenantError) {
      console.error('🏢 TenantContext: Error fetching tenant name on switch:', tenantError);
    }
    
    const tenantName = tenantData?.name || null;
    setActiveTenantName(tenantName);
    console.log('🏢 TenantContext: Switched to tenant:', tenantName, 'ID:', tenantId);
  };

  const isSwitched = activeTenantId !== userDefaultTenantId;

  return (
    <TenantContext.Provider
      value={{
        activeTenantId,
        activeTenantName,
        userDefaultTenantId,
        setActiveTenantId: handleSetActiveTenantId,
        isLoading,
        isSwitched,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenantContext() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantProvider');
  }
  return context;
}
